import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { SeverityBadge, StatusBadge, healthColor } from "../components/badges";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../context/AuthContext";

function TrendIcon({ trend }) {
  if (trend === "IMPROVING") return <TrendingUp className="h-3.5 w-3.5 text-[#16A34A]" />;
  if (trend === "WORSENING") return <TrendingDown className="h-3.5 w-3.5 text-[#DC2626]" />;
  return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
}

export default function Portfolio() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/portfolio").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  if (!data) return <Skeleton className="h-60 w-full" />;

  const canDrill = ["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"].includes(user.role);

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("portfolio")}</h1>
        <div className="text-sm text-muted-foreground">
          {t("coverage")}: <span className="font-medium text-foreground" data-testid="portfolio-coverage">
            {data.reporting_coverage.percentage}%</span> ({data.reporting_coverage.submitted}/{data.reporting_coverage.expected})
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.heatmap.map((h) => (
          <button key={h.project.id} data-testid={`heatmap-cell-${h.project.key}`}
            onClick={() => canDrill && navigate(`/projects/${h.project.id}`)}
            className="apha-card apha-hover p-4 text-left relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: healthColor(h.health.status) }} />
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs text-muted-foreground">{h.project.key} · {h.project.client}</div>
                <div className="font-heading font-semibold text-sm">{h.project.name}</div>
              </div>
              <div className="text-2xl font-heading font-semibold" style={{ color: healthColor(h.health.status) }}>
                {h.health.score}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
              <StatusBadge status={h.health.status} />
              <TrendIcon trend={h.health.trend} />
              <span>{t("coverage")}: {h.health.coverage.percentage}%</span>
              <span className="text-[#991B1B]">{h.health.open_warnings.critical + h.health.open_warnings.high} H/C</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5">
              PM: {h.project.pm}{h.project.next_milestone_date && ` · Milestone: ${h.project.next_milestone_date}`}
            </div>
          </button>
        ))}
      </div>

      <section className="apha-card p-5" data-testid="top-risks-section">
        <h2 className="font-heading font-semibold text-base mb-3">{t("topRisks")}</h2>
        <div className="space-y-2">
          {data.top_risks.map((w) => (
            <div key={w.id} className="flex flex-wrap items-center gap-2 border-b last:border-0 pb-2 last:pb-0">
              <SeverityBadge severity={w.severity} />
              {w.project_key && <span className="text-xs font-medium bg-secondary rounded px-1.5 py-0.5">{w.project_key}</span>}
              <span className="text-sm flex-1 min-w-[200px]">{w.summary}</span>
              <span className="text-xs text-muted-foreground">
                {t("owner")}: {w.owner_name || "—"} · {Math.round(w.age_hours || 0)}h
                {w.escalated_at && <span className="text-[#991B1B]"> · {t("slaBreached")}</span>}
              </span>
            </div>
          ))}
          {data.top_risks.length === 0 && <div className="text-sm text-muted-foreground">{t("noData")}</div>}
        </div>
      </section>

      {data.overdue_interventions.length > 0 && (
        <section className="apha-card p-5 border-[#FED7AA]" data-testid="overdue-interventions-section">
          <h2 className="font-heading font-semibold text-base mb-3 text-[#9A3412]">{t("overdueInterventions")}</h2>
          {data.overdue_interventions.map((i) => (
            <div key={i.id} className="text-sm py-1">
              {i.action} <span className="text-xs text-muted-foreground">— {t("owner")}: {i.owner_name} · {t("targetDate")}: {(i.target_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
